import { Platform } from '@cart-services/hooks/fp-b2b/store-map'
import { useFpB2bProductCatalogProducts } from '@cart-services/hooks/fp-b2b/use-fp-b2b-product-catalog-products'
import { CarouselCategoryPromotion } from '@cart-ui/CategoryPromotion/CarouselCategoryPromotion'
import { CategoryPromotionAnnouncement } from '@cart-ui/CategoryPromotion/CategoryPromotionAnnouncement'
import { CategoryPromotionImages } from '@cart-ui/CategoryPromotion/CategoryPromotionImages'
import type { Category } from '@cart-ui/CategoryTabs/CategoryTabs'
import { useCategoryTabs } from '@cart-ui/CategoryTabs/useCategoryTabs'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { useApiCampaignBanners } from '@hooks-api/fortune/useApiCampaignBanners'
import { Box } from '@pets-ui/Box/Box'
import { Container } from '@pets-ui/Container/Container'
import { Text } from '@pets-ui/Text/Text'
import { useMemo, useRef } from 'react'
import { useHistory } from 'react-router-dom-v5'
import { FpB2bProductCatalogProvider } from '../../fp-b2b-shared/components/ProductCatalogProvider/ProductCatalogProvider'
import { useProductCatalog } from '../../fp-b2b-shared/hooks/use-product-catalog'
import { DialogWeightAttribute } from '../../merchant/product-catalog/components/DialogWeightAttribute/DialogWeightAttribute'
import { useStickyOffset } from '../components/PageLayout/useStickyOffset'
import { useStmCampaignCode } from '../hooks'
import { CategoryChips } from './components/CategoryChips'
import { DialogDeliveryClass } from './components/DialogDeliveryClass'
import { StmCategoryProducts } from './components/StmCategoryProducts'

const ProductCatalogContent = () => {
  const history = useHistory()
  const { campaignId, campaignCode, projectCode, platform } = useProductCatalog()
  const { destructuredData: exhibitorSettings } = useApiCampaignActivityInfo(campaignCode)
  const { destructuredData: campaignBanners } = useApiCampaignBanners({ campaign_code: campaignCode })
  const { productCatalogDto, error: productCatalogError } = useFpB2bProductCatalogProducts(campaignId, projectCode, campaignCode, platform)
  const stickyOffset = useStickyOffset()
  const scrollAnchorRef = useRef<HTMLDivElement>(null)

  const categoryTabs: Category[] = useMemo(() => {
    return productCatalogDto?.products?.map(category => ({
      label: category.tab_display_name,
      value: category.tab_display_name,
      highlight: category.coupon_ids.length > 0,
    })) || []
  }, [productCatalogDto?.products])

  const { setCategory, category } = useCategoryTabs(categoryTabs)

  const handleChange = useCallbackEvent((cat: Category) => {
    if (cat.value === category) return
    setCategory(cat.value)
    history.push({ search: `?tab=${cat.value}` })
    scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })

  return (
    <Box>
      <CarouselCategoryPromotion campaignBanners={campaignBanners} />
      <CategoryPromotionImages campaignBanners={campaignBanners} />
      <Container rootRef={scrollAnchorRef} scrollMarginTop={`${stickyOffset}px`} gap={6} display="flex" flexDirection="column" pt={4}>
        <CategoryChips items={categoryTabs} value={category ?? ''} onChange={handleChange} />
        <CategoryPromotionAnnouncement
          title="最新公告"
          description={exhibitorSettings?.announcement}
        />
        {productCatalogError && (
          <Text align="center" color="grey">商品載入失敗，請稍後再試</Text>
        )}
        {!productCatalogError && category && (
          // 切換 category 時列表會瞬間替換，建立 80vh 撐住高度，避免暫時塌陷破壞 sticky，造成 banner 視覺跳動
          <Box minHeight="80vh">
            <StmCategoryProducts category={category} />
          </Box>
        )}
      </Container>
      <DialogDeliveryClass />
      <DialogWeightAttribute />
    </Box>
  )
}

export const ProductCatalog = () => {
  const { campaignId, campaignCode, projectCode, countryCode } = useStmCampaignCode()
  const { destructuredData: campaign } = useApiCampaignActivityInfo(campaignCode)

  if (!campaignId || !campaignCode || !projectCode || !countryCode || !campaign?.currency) {
    return null
  }

  return (
    <FpB2bProductCatalogProvider
      platform={Platform.STM}
      campaignId={campaignId}
      campaignCode={campaignCode}
      projectCode={projectCode}
      countryCode={countryCode}
      currency={campaign.currency}
    >
      <ProductCatalogContent />
    </FpB2bProductCatalogProvider>
  )
}
